import type {
  BusinessUnitSummary,
  FocusArea,
  GroupEntitySummary,
  ObjectiveGoal,
  ObjectiveProcess,
  KeyObjective,
  PlanTreeResponse,
  QuarterPlan,
  UserSummary
} from '@/interfaces/Objectives.interface';
import { generateQuarterOptions } from '@/components/page-components/objectives/utils/quarter-options';
import { averageProgress, progressToStatus } from '@/components/page-components/objectives/utils/progress-status';

const USERS: UserSummary[] = [
  { id: 1, full_name: 'Alex Brown' },
  { id: 2, full_name: 'Casey Davis' },
  { id: 3, full_name: 'Evan Foster' }
];

const MOCK_BUSINESS_UNITS: BusinessUnitSummary[] = [
  { id: 1, name: 'Partnerships' },
  { id: 2, name: 'Customers' },
  { id: 3, name: 'Digital & Media' },
  { id: 4, name: 'Capital & Innovation' },
  { id: 5, name: 'International' },
  { id: 6, name: 'Platform' }
];

const MOCK_GROUP_ENTITIES: GroupEntitySummary[] = [
  { id: 1, name: 'Group' },
  { id: 2, name: 'Partners' },
  { id: 3, name: 'Money' },
  { id: 4, name: 'Cedar' },
  { id: 5, name: 'Markets' },
  { id: 6, name: '1Edge' },
  { id: 7, name: 'Capital' },
  { id: 8, name: 'Save More Money' },
  { id: 9, name: 'Small Business' },
  { id: 10, name: 'Risk' },
  { id: 11, name: 'Jaywing' }
];

export function mockBusinessUnits(): BusinessUnitSummary[] {
  return MOCK_BUSINESS_UNITS;
}

export function mockGroupEntities(): GroupEntitySummary[] {
  return MOCK_GROUP_ENTITIES;
}

function resolveBusinessUnits(ids: number[]): BusinessUnitSummary[] {
  return ids
    .map((id) => MOCK_BUSINESS_UNITS.find((unit) => unit.id === id))
    .filter((unit): unit is BusinessUnitSummary => unit != null);
}

function resolveGroupEntities(ids: number[]): GroupEntitySummary[] {
  return ids
    .map((id) => MOCK_GROUP_ENTITIES.find((entity) => entity.id === id))
    .filter((entity): entity is GroupEntitySummary => entity != null);
}

let nextId = 100;
function uid() {
  nextId += 1;
  return nextId;
}

function userById(id: number | null | undefined): UserSummary | null {
  if (id == null) return null;
  return USERS.find((user) => user.id === id) ?? null;
}

function recalcKeyObjective(ko: KeyObjective): KeyObjective {
  const goalProgress = ko.goals.map((goal) => goal.progress_percent);
  const progress_percent = averageProgress(goalProgress);
  return {
    ...ko,
    progress_percent,
    status: progressToStatus(progress_percent)
  };
}

function recalcFocusArea(focusArea: FocusArea): FocusArea {
  return {
    ...focusArea,
    key_objectives: focusArea.key_objectives.map(recalcKeyObjective)
  };
}

const quarterPlans: QuarterPlan[] = generateQuarterOptions();

const focusAreasByQuarter = new Map<number, FocusArea[]>();

function seedQuarter(quarterPlanId: number) {
  if (focusAreasByQuarter.has(quarterPlanId)) return;

  const current = quarterPlans.find((plan) => plan.id === quarterPlanId);
  if (!current?.is_current) {
    focusAreasByQuarter.set(quarterPlanId, []);
    return;
  }

  const goal1: ObjectiveGoal = {
    id: uid(),
    key_objective_id: 0,
    name: 'Launch partner pipeline',
    time_frame: current.label,
    assignee_user_id: 1,
    assignee: userById(1),
    progress_percent: 45,
    status: progressToStatus(45),
    tracking: {
      pipeline_id: 1,
      pipeline_name: 'Partner growth',
      milestone_stage_id: 3,
      milestone_stage_name: 'Won',
      target_task_count: 20,
      team_member_ids: [1, 2],
      current_task_count: 9
    },
    business_unit_ids: [1, 2],
    business_units: resolveBusinessUnits([1, 2]),
    group_entity_ids: [5],
    group_entities: resolveGroupEntities([5]),
    sort_order: 0
  };
  goal1.key_objective_id = 10;

  const process1: ObjectiveProcess = {
    id: uid(),
    key_objective_id: 10,
    name: 'Quarterly compliance review',
    time_frame: current.label,
    assignee_user_id: 2,
    assignee: userById(2),
    progress_percent: 20,
    status: progressToStatus(20),
    reminder_ids: [],
    business_unit_ids: [],
    business_units: [],
    group_entity_ids: [],
    group_entities: [],
    sort_order: 0
  };

  const keyObjective: KeyObjective = recalcKeyObjective({
    id: 10,
    focus_area_id: 2,
    name: 'Grow revenue',
    time_frame: current.label,
    assignee_user_id: 1,
    assignee: userById(1),
    progress_percent: 0,
    status: 'not_started',
    sort_order: 0,
    goals: [goal1],
    processes: [process1]
  });

  focusAreasByQuarter.set(quarterPlanId, [
    recalcFocusArea({
      id: 1,
      quarter_plan_id: quarterPlanId,
      name: 'Revenue growth',
      sort_order: 0,
      key_objectives: []
    }),
    recalcFocusArea({
      id: 2,
      quarter_plan_id: quarterPlanId,
      name: 'Grow partner revenue',
      sort_order: 1,
      key_objectives: [keyObjective]
    })
  ]);
}

quarterPlans.forEach((plan) => seedQuarter(plan.id));

export function mockGetQuarterPlans(): QuarterPlan[] {
  return quarterPlans;
}

export function mockGetPlanTree(quarterPlanId: number): PlanTreeResponse {
  seedQuarter(quarterPlanId);
  const quarter_plan =
    quarterPlans.find((plan) => plan.id === quarterPlanId) ?? quarterPlans[0];
  return {
    quarter_plan,
    focus_areas: focusAreasByQuarter.get(quarterPlanId) ?? []
  };
}

export function mockCreateFocusArea(
  quarterPlanId: number,
  name: string
): FocusArea {
  seedQuarter(quarterPlanId);
  const list = focusAreasByQuarter.get(quarterPlanId) ?? [];
  const focusArea: FocusArea = {
    id: uid(),
    quarter_plan_id: quarterPlanId,
    name,
    sort_order: list.length,
    key_objectives: []
  };
  list.push(focusArea);
  focusAreasByQuarter.set(quarterPlanId, list);
  return focusArea;
}

export function mockUpdateFocusArea(id: number, name: string): FocusArea {
  for (const [quarterId, areas] of Array.from(focusAreasByQuarter.entries())) {
    const index = areas.findIndex((area) => area.id === id);
    if (index >= 0) {
      areas[index] = { ...areas[index], name };
      focusAreasByQuarter.set(quarterId, areas);
      return areas[index];
    }
  }
  throw new Error('Focus area not found');
}

export function mockDeleteFocusArea(id: number): void {
  for (const [quarterId, areas] of Array.from(focusAreasByQuarter.entries())) {
    focusAreasByQuarter.set(
      quarterId,
      areas.filter((area) => area.id !== id)
    );
  }
}

function findKeyObjective(id: number): {
  quarterId: number;
  focusArea: FocusArea;
  keyObjective: KeyObjective;
} | null {
  for (const [quarterId, areas] of Array.from(focusAreasByQuarter.entries())) {
    for (const focusArea of areas) {
      const keyObjective = focusArea.key_objectives.find((ko) => ko.id === id);
      if (keyObjective) {
        return { quarterId, focusArea, keyObjective };
      }
    }
  }
  return null;
}

export function mockCreateKeyObjective(
  focusAreaId: number,
  payload: { name: string; time_frame: string; assignee_user_id?: number | null }
): KeyObjective {
  for (const [quarterId, areas] of Array.from(focusAreasByQuarter.entries())) {
    const focusIndex = areas.findIndex((area) => area.id === focusAreaId);
    if (focusIndex < 0) continue;
    const keyObjective: KeyObjective = recalcKeyObjective({
      id: uid(),
      focus_area_id: focusAreaId,
      name: payload.name,
      time_frame: payload.time_frame,
      assignee_user_id: payload.assignee_user_id ?? null,
      assignee: userById(payload.assignee_user_id),
      progress_percent: 0,
      status: 'not_started',
      sort_order: areas[focusIndex].key_objectives.length,
      goals: [],
      processes: []
    });
    areas[focusIndex].key_objectives.push(keyObjective);
    focusAreasByQuarter.set(quarterId, areas);
    return keyObjective;
  }
  throw new Error('Focus area not found');
}

export function mockUpdateKeyObjective(
  id: number,
  payload: Partial<{
    name: string;
    time_frame: string;
    assignee_user_id: number | null;
    focus_area_id: number;
  }>
): KeyObjective {
  const found = findKeyObjective(id);
  if (!found) throw new Error('Key objective not found');

  if (payload.focus_area_id && payload.focus_area_id !== found.focusArea.id) {
    mockMoveKeyObjective(id, payload.focus_area_id);
    return mockUpdateKeyObjective(id, {
      name: payload.name,
      time_frame: payload.time_frame,
      assignee_user_id: payload.assignee_user_id ?? undefined
    });
  }

  const updated = recalcKeyObjective({
    ...found.keyObjective,
    ...payload,
    assignee: userById(payload.assignee_user_id ?? found.keyObjective.assignee_user_id)
  });

  const areas = focusAreasByQuarter.get(found.quarterId) ?? [];
  const focusIndex = areas.findIndex((area) => area.id === found.focusArea.id);
  const koIndex = areas[focusIndex].key_objectives.findIndex((ko) => ko.id === id);
  areas[focusIndex].key_objectives[koIndex] = updated;
  focusAreasByQuarter.set(found.quarterId, areas);
  return updated;
}

function mockMoveKeyObjective(id: number, focusAreaId: number) {
  const found = findKeyObjective(id);
  if (!found) throw new Error('Key objective not found');

  const areas = focusAreasByQuarter.get(found.quarterId) ?? [];
  const fromIndex = areas.findIndex((area) => area.id === found.focusArea.id);
  const toIndex = areas.findIndex((area) => area.id === focusAreaId);
  if (toIndex < 0) throw new Error('Target focus area not found');

  areas[fromIndex].key_objectives = areas[fromIndex].key_objectives.filter(
    (ko) => ko.id !== id
  );
  const moved = { ...found.keyObjective, focus_area_id: focusAreaId };
  areas[toIndex].key_objectives.push(moved);
  focusAreasByQuarter.set(found.quarterId, areas);
}

export function mockCreateGoal(
  keyObjectiveId: number,
  payload: {
    name: string;
    time_frame: string;
    assignee_user_id?: number | null;
    tracking?: ObjectiveGoal['tracking'];
    business_unit_ids?: number[];
    group_entity_ids?: number[];
  }
): ObjectiveGoal {
  const found = findKeyObjective(keyObjectiveId);
  if (!found) throw new Error('Key objective not found');

  const goal: ObjectiveGoal = {
    id: uid(),
    key_objective_id: keyObjectiveId,
    name: payload.name,
    time_frame: payload.time_frame,
    assignee_user_id: payload.assignee_user_id ?? null,
    assignee: userById(payload.assignee_user_id),
    progress_percent: payload.tracking
      ? Math.round(
          (payload.tracking.current_task_count /
            Math.max(payload.tracking.target_task_count, 1)) *
            100
        )
      : 0,
    status: progressToStatus(0),
    tracking: payload.tracking ?? null,
    business_unit_ids: payload.business_unit_ids ?? [],
    business_units: resolveBusinessUnits(payload.business_unit_ids ?? []),
    group_entity_ids: payload.group_entity_ids ?? [],
    group_entities: resolveGroupEntities(payload.group_entity_ids ?? []),
    sort_order: found.keyObjective.goals.length
  };
  goal.status = progressToStatus(goal.progress_percent);

  const areas = focusAreasByQuarter.get(found.quarterId) ?? [];
  const focusIndex = areas.findIndex((area) => area.id === found.focusArea.id);
  const koIndex = areas[focusIndex].key_objectives.findIndex(
    (ko) => ko.id === keyObjectiveId
  );
  areas[focusIndex].key_objectives[koIndex].goals.push(goal);
  areas[focusIndex].key_objectives[koIndex] = recalcKeyObjective(
    areas[focusIndex].key_objectives[koIndex]
  );
  focusAreasByQuarter.set(found.quarterId, areas);
  return goal;
}

export function mockUpdateGoal(
  id: number,
  payload: Partial<ObjectiveGoal>
): ObjectiveGoal {
  for (const [quarterId, areas] of Array.from(focusAreasByQuarter.entries())) {
    for (const focusArea of areas) {
      for (const ko of focusArea.key_objectives) {
        const goalIndex = ko.goals.findIndex((goal) => goal.id === id);
        if (goalIndex < 0) continue;
        const updated: ObjectiveGoal = {
          ...ko.goals[goalIndex],
          ...payload,
          assignee: userById(
            payload.assignee_user_id ?? ko.goals[goalIndex].assignee_user_id
          )
        };
        if (payload.business_unit_ids != null) {
          updated.business_units = resolveBusinessUnits(payload.business_unit_ids);
        }
        if (payload.group_entity_ids != null) {
          updated.group_entities = resolveGroupEntities(payload.group_entity_ids);
        }
        if (updated.tracking) {
          updated.progress_percent = Math.round(
            (updated.tracking.current_task_count /
              Math.max(updated.tracking.target_task_count, 1)) *
              100
          );
          updated.status = progressToStatus(updated.progress_percent);
        }
        ko.goals[goalIndex] = updated;
        const focusIndex = areas.findIndex((area) => area.id === focusArea.id);
        const koIndex = areas[focusIndex].key_objectives.findIndex(
          (item) => item.id === ko.id
        );
        areas[focusIndex].key_objectives[koIndex] = recalcKeyObjective(ko);
        focusAreasByQuarter.set(quarterId, areas);
        return updated;
      }
    }
  }
  throw new Error('Goal not found');
}

export function mockCreateProcess(
  keyObjectiveId: number,
  payload: {
    name: string;
    time_frame: string;
    assignee_user_id?: number | null;
    business_unit_ids?: number[];
    group_entity_ids?: number[];
  }
): ObjectiveProcess {
  const found = findKeyObjective(keyObjectiveId);
  if (!found) throw new Error('Key objective not found');

  const process: ObjectiveProcess = {
    id: uid(),
    key_objective_id: keyObjectiveId,
    name: payload.name,
    time_frame: payload.time_frame,
    assignee_user_id: payload.assignee_user_id ?? null,
    assignee: userById(payload.assignee_user_id),
    progress_percent: 0,
    status: 'not_started',
    reminder_ids: [],
    business_unit_ids: payload.business_unit_ids ?? [],
    business_units: resolveBusinessUnits(payload.business_unit_ids ?? []),
    group_entity_ids: payload.group_entity_ids ?? [],
    group_entities: resolveGroupEntities(payload.group_entity_ids ?? []),
    sort_order: found.keyObjective.processes.length
  };

  const areas = focusAreasByQuarter.get(found.quarterId) ?? [];
  const focusIndex = areas.findIndex((area) => area.id === found.focusArea.id);
  const koIndex = areas[focusIndex].key_objectives.findIndex(
    (ko) => ko.id === keyObjectiveId
  );
  areas[focusIndex].key_objectives[koIndex].processes.push(process);
  focusAreasByQuarter.set(found.quarterId, areas);
  return process;
}

function findProcess(id: number): {
  quarterId: number;
  focusArea: FocusArea;
  keyObjective: KeyObjective;
  processIndex: number;
  process: ObjectiveProcess;
} | null {
  for (const [quarterId, areas] of Array.from(focusAreasByQuarter.entries())) {
    for (const focusArea of areas) {
      for (const ko of focusArea.key_objectives) {
        const processIndex = ko.processes.findIndex((item) => item.id === id);
        if (processIndex >= 0) {
          return {
            quarterId,
            focusArea,
            keyObjective: ko,
            processIndex,
            process: ko.processes[processIndex]
          };
        }
      }
    }
  }
  return null;
}

export function mockUpdateProcess(
  id: number,
  payload: Partial<{
    name: string;
    time_frame: string;
    key_objective_id: number;
    assignee_user_id: number | null;
    business_unit_ids: number[];
    group_entity_ids: number[];
  }>
): ObjectiveProcess {
  const found = findProcess(id);
  if (!found) throw new Error('Process not found');

  if (
    payload.key_objective_id != null &&
    payload.key_objective_id !== found.keyObjective.id
  ) {
    const areas = focusAreasByQuarter.get(found.quarterId) ?? [];
    const fromFocusIndex = areas.findIndex(
      (area) => area.id === found.focusArea.id
    );
    const fromKoIndex = areas[fromFocusIndex].key_objectives.findIndex(
      (ko) => ko.id === found.keyObjective.id
    );
    const [moved] = areas[fromFocusIndex].key_objectives[
      fromKoIndex
    ].processes.splice(found.processIndex, 1);

    const target = findKeyObjective(payload.key_objective_id);
    if (!target) throw new Error('Target key objective not found');

    const toFocusIndex = areas.findIndex(
      (area) => area.id === target.focusArea.id
    );
    const toKoIndex = areas[toFocusIndex].key_objectives.findIndex(
      (ko) => ko.id === payload.key_objective_id
    );
    moved.key_objective_id = payload.key_objective_id;
    areas[toFocusIndex].key_objectives[toKoIndex].processes.push(moved);
    areas[fromFocusIndex].key_objectives[fromKoIndex] = recalcKeyObjective(
      areas[fromFocusIndex].key_objectives[fromKoIndex]
    );
    areas[toFocusIndex].key_objectives[toKoIndex] = recalcKeyObjective(
      areas[toFocusIndex].key_objectives[toKoIndex]
    );
    focusAreasByQuarter.set(found.quarterId, areas);

    const { key_objective_id: _moved, ...rest } = payload;
    if (Object.keys(rest).length === 0) {
      return moved;
    }
    return mockUpdateProcess(id, rest);
  }

  const updated: ObjectiveProcess = {
    ...found.process,
    ...payload,
    assignee:
      payload.assignee_user_id !== undefined
        ? userById(payload.assignee_user_id)
        : found.process.assignee
  };
  if (payload.business_unit_ids != null) {
    updated.business_units = resolveBusinessUnits(payload.business_unit_ids);
  }
  if (payload.group_entity_ids != null) {
    updated.group_entities = resolveGroupEntities(payload.group_entity_ids);
  }

  const areas = focusAreasByQuarter.get(found.quarterId) ?? [];
  const focusIndex = areas.findIndex((area) => area.id === found.focusArea.id);
  const koIndex = areas[focusIndex].key_objectives.findIndex(
    (ko) => ko.id === found.keyObjective.id
  );
  areas[focusIndex].key_objectives[koIndex].processes[found.processIndex] =
    updated;
  areas[focusIndex].key_objectives[koIndex] = recalcKeyObjective(
    areas[focusIndex].key_objectives[koIndex]
  );
  focusAreasByQuarter.set(found.quarterId, areas);
  return updated;
}

export function mockGetAssignedGoals(): ObjectiveGoal[] {
  const current = quarterPlans.find((plan) => plan.is_current);
  if (!current) return [];
  const tree = mockGetPlanTree(current.id);
  return tree.focus_areas.flatMap((area) =>
    area.key_objectives.flatMap((ko) => ko.goals)
  );
}

export function mockGetAssignedProcesses(): ObjectiveProcess[] {
  const current = quarterPlans.find((plan) => plan.is_current);
  if (!current) return [];
  const tree = mockGetPlanTree(current.id);
  return tree.focus_areas.flatMap((area) =>
    area.key_objectives.flatMap((ko) => ko.processes)
  );
}
